import type { ReactElement, ReactNode } from 'react'
import { isDefined } from '@terros/common'
import { useNavigate, createFileRoute, useMatch, Outlet } from '@tanstack/react-router'
import { Group, Modal, type ModalProps, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import { Size, useSafeCurrentWorkflow, useTranslation } from '@front/model'
import { TDrawer, Page, type TDrawerProps } from '@components'
import { CalendarWeekView } from './WeekView'
import { useSelectedEvent } from './useSelectedEvent'
import { useCalendarState } from './state'
import { useCalendarColumnState } from './listView/useCalendarColumnState'
import { TListView } from './listView/TListView'
import { FilterStatus } from './FilterStatus'
import { EditEventForm } from './editEvent/EditEventForm'
import { DownloadEventsCsv } from './DownloadEvents'
import { CalendarDayView } from './dayView'
import { ViewOptions } from './customize/ViewOptions'
import { FiltersDrawer } from './customize'
import { CalendarHeading } from './CalendarHeading'

function Calendar(): ReactElement {
  const { t } = useTranslation()

  const calendarState = useCalendarState()
  useToastError(calendarState.error)
  const columnState = useCalendarColumnState()

  const { viewRange: calendarView, search, setSearch, data, viewMode: calendarMode } = calendarState

  const CalendarView = calendarView === 'week' ? CalendarWeekView : CalendarDayView

  return (
    <Page>
      <CalendarHeading {...calendarState} />
      <Page.FunctionBar>
        <Group gap={Size.space['2xl']}>
          <FiltersDrawer {...calendarState} />
          <TextInput placeholder='Filter' value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
          <FilterStatus calendarState={calendarState} />
        </Group>
        <Group gap={Size.space['2xl']}>
          <ViewOptions
            {...calendarState}
            listViewGroupByProps={{
              groupBy: calendarState.groupBy,
              setGroupBy: calendarState.setGroupBy,
              ...columnState,
            }}
          />
          <DownloadEventsCsv data={data} />
        </Group>
      </Page.FunctionBar>
      <Page.Content>
        {calendarMode === 'list' ? (
          <TListView {...calendarState} selectedColumns={columnState.selectedColumns} />
        ) : (
          <CalendarView {...calendarState} />
        )}
      </Page.Content>
      <CalendarEventDrawer />
    </Page>
  )
}

const CalendarEventDrawer = (): ReactNode => {
  const wfContext = useSafeCurrentWorkflow()
  const { selectedEventId, setSelectedEventId } = useSelectedEvent()

  const navigate = useNavigate()
  const hasChildRoute = useMatch({ from: '/calendar/$', shouldThrow: false })

  const Comp = isDefined(wfContext) ? EventModal : EventDrawer

  return (
    <>
      <Comp opened={isDefined(selectedEventId)} onClose={() => setSelectedEventId(null)}>
        {isDefined(selectedEventId) && <EditEventForm eventId={selectedEventId} />}
      </Comp>
      <Comp
        opened={isDefined(hasChildRoute)}
        onClose={() => {
          navigate({ to: '/calendar' })
        }}
      >
        <Outlet />
      </Comp>
    </>
  )
}

const EventDrawer = (props: TDrawerProps): ReactNode => {
  return <TDrawer position='right' size='lg' title='Edit Event' {...props} />
}

const EventModal = (props: ModalProps): ReactNode => {
  return (
    <Modal
      size='2xl'
      classNames={{
        body: 'w-[80vw] max-w-[1440px] h-[90vh]',
        content: 'overflow-x-hidden',
      }}
      withCloseButton={false}
      {...props}
    />
  )
}

export const Route = createFileRoute('/calendar/')({
  component: Calendar,
})
